import { AnimatePresence, motion } from 'framer-motion';
import {
  BarChart3,
  CheckCircle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Cpu,
  Globe,
  MapPin,
  MessageSquare,
  Pin,
  Play,
  Users,
  Zap
} from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { usePreview } from '../../hooks/usePreview';
import { usePesaParcours } from '../../stores/usePesaParcours';
import MobileNarratorDrawer from './MobileNarratorDrawer';
import NeuralNarrator from './NeuralNarrator';
import QuizEngine from './QuizEngine';
import VocalOrTextInput from './VocalOrTextInput';

// Hook utilitaire mobile
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 1024);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
};

const CapstoneWarRoom = ({ data: propData }) => {
  const { darkMode } = useTheme();
  const { isPreview } = usePreview();
  const isMobile = useIsMobile();

  // ── Store ──────────────────────────────────────────────────────────────────
  const {
    setCapstoneFocus,
    triggerMediator,
    activeItem,
    closeNarrator,
    toggleShowNarrator,
    setEmbeddedMedia,
    clearEmbeddedMedia,
    resolveAsset,
    harvestStepData,
    validateEvidence,
    embeddedMedia,
  } = usePesaParcours();

  const showNarrator = usePesaParcours(s => s.ui?.showNarrator ?? false);

  // ── Données seed ───────────────────────────────────────────────────────────
  const d = useMemo(() => propData || activeItem?.data || {}, [propData, activeItem]);
  const steps           = d.resources?.steps || [];
  const rt              = d.realTime || {};
  const config          = d.centralWorkspace?.config || {};
  const liveSession     = d.liveSession || {};
  const mediatorOnStart = d.mediatorOnStart || null;

  // ── State local ────────────────────────────────────────────────────────────
  const [activeStep, setActiveStep]             = useState(0);
  const [localAnswers, setLocalAnswers]         = useState({});
  const [selectedEmotions, setSelectedEmotions] = useState([]);
  const [isSubmitting, setIsSubmitting]         = useState(false);
  const [isComplete, setIsComplete]             = useState(false);
  const [isTransitioning, setIsTransitioning]   = useState(false);
  const [asideOpen, setAsideOpen]               = useState(false); // mobile only
  const [narratorOpen, setNarratorOpen]         = useState(false); // mobile only
  const startTime                               = useRef(Date.now());

  // ── Lifecycle ──────────────────────────────────────────────────────────────
  useEffect(() => {
    setCapstoneFocus(true);
    return () => setCapstoneFocus(false);
  }, []);

  useEffect(() => {
    if (mediatorOnStart && !showNarrator) toggleShowNarrator();
  }, [mediatorOnStart]); // eslint-disable-line

  useEffect(() => {
    setSelectedEmotions([]);
  }, [activeStep]);

  // ── Guards ─────────────────────────────────────────────────────────────────
  if (!d.titre) {
    return (
      <div className="p-20 font-mono text-red-500 text-center">
        [ERREUR] : Données capstone introuvables
      </div>
    );
  }

  const current = steps[activeStep] || {};
  const isFirst = activeStep === 0;
  const isLast  = activeStep === steps.length - 1;

  // ── Harvest helpers ────────────────────────────────────────────────────────
  const harvest = (stepId, value) => {
    const richPayload = typeof value === 'string'
      ? { type: 'text', text: value, ts: Date.now() }
      : { ...value, ts: Date.now() };
    setLocalAnswers(prev => ({ ...prev, [stepId]: richPayload }));
    if (!isPreview) harvestStepData(stepId, richPayload);
  };

  const handleVocalSubmit = (stepId) => async ({ text, mediaBase64, mediaType, dureeEnreg }) => {
    harvest(stepId, { type: mediaType || 'text', text, mediaBase64, mediaType, duree: dureeEnreg || 0 });
  };

  // ── Navigation ─────────────────────────────────────────────────────────────
  const handleNext = () => {
    if (isTransitioning || isLast) return;
    setIsTransitioning(true);
    setTimeout(() => { setActiveStep(i => i + 1); setIsTransitioning(false); }, 300);
  };

  const handleBack = () => {
    if (isTransitioning || isFirst) return;
    setIsTransitioning(true);
    setTimeout(() => { setActiveStep(i => i - 1); setIsTransitioning(false); }, 300);
  };

  // ── Soumission finale ──────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    const timeSpent = Math.round((Date.now() - startTime.current) / 1000);
    if (!isPreview) {
      try {
        await validateEvidence(activeItem?.id || d.id || 'capstone', {
          type: 'activity', timeSpent,
          expectedDuration: (d.duration || 60) * 60,
          score: 100, passed: true, harvest: localAnswers,
        });
      } catch (err) {
        console.error('[Capstone] validateEvidence error:', err);
      }
    } else {
      console.log('%c[PREVIEW] Capstone harvest:', 'color:#f59e0b;font-weight:bold', localAnswers);
    }
    setIsComplete(true);
    setIsSubmitting(false);
  };

  // ── Styles partagés ────────────────────────────────────────────────────────
  const dk = darkMode;
  const C = {
    bg:       dk ? 'bg-[#090b0f]' : 'bg-slate-50',
    surface:  dk ? 'bg-[#0f1117]' : 'bg-white',
    border:   dk ? 'border-white/[0.06]' : 'border-slate-200',
    text:     dk ? 'text-slate-300' : 'text-slate-600',
    title:    dk ? 'text-white' : 'text-slate-900',
    accent:   dk ? 'text-amber-400' : 'text-amber-600',
    accentBg: dk ? 'bg-amber-400/10 border-amber-400/20' : 'bg-amber-50 border-amber-200',
    muted:    dk ? 'text-slate-500' : 'text-slate-400',
    label:    'text-[9px] font-black uppercase tracking-[0.3em] font-mono',
  };

  // ─────────────────────────────────────────────────────────────────────────
  // ÉCRAN DE COMPLÉTION
  // ─────────────────────────────────────────────────────────────────────────
  if (isComplete) {
    return (
      <div className={`h-full w-full flex items-center justify-center ${C.bg}`}>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ type: 'spring', duration: 0.7 }}
          className="max-w-xl text-center px-8"
        >
          <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-amber-500/10 border-2 border-amber-500/40 flex items-center justify-center mx-auto mb-8 sm:mb-10">
            <CheckCircle size={40} className="text-amber-400" />
          </div>
          <p className={`${C.label} ${C.accent} mb-4`}>Synthèse_Soumise</p>
          <h2 className={`text-2xl sm:text-4xl font-black ${C.title} mb-4 sm:mb-6 leading-tight tracking-tighter`}>
            Votre engagement est enregistré.
          </h2>
          <p className={`${C.text} text-base sm:text-lg leading-relaxed mb-4`}>
            Vos réflexions, vos choix et votre plan d'action font désormais partie
            du portfolio de votre parcours.
          </p>
          <p className={`${C.muted} text-sm font-mono`}>
            Rendez-vous le {liveSession.date} à {liveSession.time} pour la grande finale nationale.
          </p>
        </motion.div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // CONTENU CENTRAL — partagé mobile/desktop
  // ─────────────────────────────────────────────────────────────────────────
  const StepContent = () => (
    <AnimatePresence mode="wait">
      <motion.div
        key={activeStep}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -12 }}
        transition={{ duration: 0.25 }}
        className="space-y-8 sm:space-y-12"
      >
        {/* En-tête step */}
        <header className="space-y-3 sm:space-y-4 border-l-4 border-amber-500 pl-5 sm:pl-8">
          <div className="flex items-center gap-3">
            <span className={`${C.label} ${C.accent}`}>{current.type}</span>
            <div className={`h-px flex-1 ${dk ? 'bg-white/5' : 'bg-slate-100'}`} />
            <span className={`${C.label} ${C.muted}`}>
              {current.duration ? `~${current.duration}s` : ''}
            </span>
          </div>
          <h2 className={`text-xl sm:text-3xl font-black tracking-tighter leading-tight uppercase ${C.title}`}>
            {current.title}
          </h2>
          {(current.text || current.introduction) && (
            <p className={`text-base sm:text-lg leading-relaxed font-sans normal-case ${C.text}`}>
              {current.text || current.introduction}
            </p>
          )}
        </header>

        {/* ── INPUT VOCAL / TEXTE ── */}
        {current.input === 'vocal-preferred' && (
          <div className={`p-1 rounded-[2rem] ${dk ? 'bg-gradient-to-r from-amber-500/30 to-orange-500/20' : 'bg-gradient-to-r from-amber-200 to-orange-100'}`}>
            <div className={`rounded-[1.9rem] p-5 sm:p-8 ${C.surface}`}>
              <VocalOrTextInput
                stepId={current.stepId || `step-${activeStep}`}
                label={current.placeholder || 'Saisir votre réflexion...'}
                onSubmit={handleVocalSubmit(current.stepId || `step-${activeStep}`)}
              />
              {current.validationMessage && localAnswers[current.stepId] && (
                <motion.p
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`mt-6 text-sm font-sans italic ${C.accent} border-l-2 border-amber-500/40 pl-4`}
                >
                  {current.validationMessage}
                </motion.p>
              )}
            </div>
          </div>
        )}

        {/* ── CHECKLIST ÉMOTIONNELLE ── */}
        {current.type === 'emotional-checklist' && current.options && (
          <div className="space-y-4 sm:space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {current.options.map((opt, i) => {
                const selected = selectedEmotions.includes(opt);
                return (
                  <button
                    key={i}
                    onClick={() => {
                      const next = selected
                        ? selectedEmotions.filter(x => x !== opt)
                        : [...selectedEmotions, opt];
                      setSelectedEmotions(next);
                      harvest(current.stepId, { type: 'emotions', selected: next });
                    }}
                    className={`p-4 sm:p-5 border-2 rounded-2xl text-left text-sm font-black uppercase tracking-tight transition-all flex justify-between items-center active:scale-[0.98]
                      ${selected
                        ? `border-amber-500 ${dk ? 'bg-amber-500/10 text-amber-300' : 'bg-amber-50 text-amber-700'}`
                        : `${C.border} ${dk ? 'bg-white/[0.02] text-slate-400 hover:border-amber-500/30' : 'bg-slate-50 text-slate-500 hover:border-amber-300'}`
                      }`}
                  >
                    {opt}
                    <div className={`w-4 h-4 rounded-full border-2 transition-all flex items-center justify-center shrink-0 ml-2
                      ${selected ? 'border-amber-500 bg-amber-500' : 'border-current opacity-20'}`}>
                      {selected && <CheckCircle size={10} className="text-black" />}
                    </div>
                  </button>
                );
              })}
            </div>
            <VocalOrTextInput
              stepId={`${current.stepId}-annotation`}
              label="Préciser si nécessaire (optionnel)"
              onSubmit={handleVocalSubmit(`${current.stepId}-annotation`)}
            />
          </div>
        )}

        {/* ── PROGRESSIVE REVEAL / CARDS ── */}
        {current.type === 'progressive-reveal' && current.cards && (
          <div className="space-y-4">
            {current.introduction && (
              <p className={`text-base font-sans italic normal-case ${C.muted} mb-4 sm:mb-6`}>
                {current.introduction}
              </p>
            )}
            {current.cards.map((card, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.12 }}
                className={`flex gap-4 sm:gap-6 items-start p-5 sm:p-8 rounded-2xl border ${C.border} ${dk ? 'bg-white/[0.02]' : 'bg-white'}`}
              >
                <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl ${C.accentBg} border flex items-center justify-center shrink-0`}>
                  {i === 0 ? <BarChart3 size={18} className={C.accent} />
                    : i === 1 ? <Cpu size={18} className={C.accent} />
                    : <Users size={18} className={C.accent} />}
                </div>
                <div className="space-y-1.5 min-w-0">
                  <h4 className={`text-sm font-black uppercase tracking-tight ${C.accent}`}>{card.title}</h4>
                  <p className={`text-sm font-sans normal-case leading-relaxed ${C.text}`}>{card.content}</p>
                </div>
              </motion.div>
            ))}
            {current.finalQuestion && (
              <div className={`pt-6 sm:pt-8 border-t ${C.border} mt-6 sm:mt-8`}>
                <p className={`font-black text-base sm:text-lg uppercase tracking-tight mb-5 sm:mb-6 ${C.title}`}>
                  {current.finalQuestion}
                </p>
                <VocalOrTextInput
                  stepId={`${current.stepId}-final`}
                  label="Consigner votre analyse"
                  onSubmit={handleVocalSubmit(`${current.stepId}-final`)}
                />
              </div>
            )}
          </div>
        )}

        {/* ── MICRO-QUIZ ── */}
        {current.type === 'micro-quiz' && current.questions && (
          <div className={`rounded-[2rem] border ${C.border} overflow-hidden`}>
            <div className={`px-5 sm:px-8 py-4 sm:py-5 border-b ${C.border} ${dk ? 'bg-white/[0.02]' : 'bg-amber-50/50'} flex items-center gap-3`}>
              <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
              <span className={`${C.label} ${C.accent}`}>Vérification_de_compréhension</span>
            </div>
            <div className={`p-4 sm:p-6 ${C.surface}`}>
              <QuizEngine
                quizData={{
                  questions: current.questions.map((q, idx) => ({
                    ...q,
                    _id: q._id || `capstone-micro-${activeStep}-${idx}`,
                    options: Array.isArray(q.options)
                      ? q.options.map((opt, oIdx) => ({
                          text:      typeof opt === 'string' ? opt : opt.text || '',
                          isCorrect: opt.isCorrect || q.correctAnswer === oIdx || false,
                        }))
                      : q.type === 'true-false'
                        ? [
                            { text: 'Vrai', isCorrect: q.correctAnswer === true },
                            { text: 'Faux', isCorrect: q.correctAnswer === false },
                          ]
                        : [],
                  })),
                  passingScore: current.passingScore || 100,
                }}
                mode="micro"
                embedded={true}
                onComplete={() => harvest(`${current.stepId}-quiz`, { type: 'quiz', passed: true, score: 100 })}
                onFail={() => harvest(`${current.stepId}-quiz`, { type: 'quiz', passed: false })}
              />
            </div>
          </div>
        )}

        {/* ── THEORY INVITATION ── */}
        {current.type === 'theory-invitation' && current.mediaReference && (
          <div className={`p-1 rounded-[2rem] ${dk ? 'bg-gradient-to-br from-white/5 to-transparent' : 'bg-gradient-to-br from-amber-100 to-transparent'}`}>
            <div className={`p-6 sm:p-10 rounded-[1.9rem] border ${C.border} ${C.surface} flex flex-col items-center text-center gap-6 sm:gap-8`}>
              <div className={`w-16 h-16 sm:w-20 sm:h-20 rounded-full ${C.accentBg} border-2 border-amber-500/40 flex items-center justify-center`}>
                <Play size={28} className={`${C.accent} ml-1`} fill="currentColor" />
              </div>
              <div className="space-y-2">
                <h4 className={`text-xl sm:text-2xl font-black uppercase tracking-tight ${C.title}`}>
                  {current.mediaReference.title}
                </h4>
                <p className={`max-w-sm font-sans normal-case ${C.muted} text-sm leading-relaxed`}>
                  {current.mediaReference.description}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-3 sm:gap-4 w-full sm:w-auto">
                {current.actions?.map((act, i) => (
                  <button
                    key={i}
                    onClick={() => {
                      if (act.action === 'play-embedded-video' && current.mediaReference?.file) {
                        setEmbeddedMedia({ type: 'video', src: resolveAsset(current.mediaReference.file), title: current.mediaReference.title });
                      } else if (act.action === 'open-resource-panel' && current.resourceReference?.file) {
                        setEmbeddedMedia({ type: 'pdf', src: resolveAsset(current.resourceReference.file), title: current.resourceReference.title });
                      }
                    }}
                    className={`w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 rounded-2xl font-black text-[10px] uppercase tracking-widest transition-all active:scale-95
                      ${i === 0
                        ? 'bg-amber-500 text-black hover:bg-amber-400 shadow-lg shadow-amber-900/20'
                        : `border ${C.border} ${C.text} hover:border-amber-500/40`
                      }`}
                  >
                    {act.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── PERSONAL SYNTHESIS ── */}
        {current.type === 'personal-synthesis' && (
          <div className={`p-5 sm:p-8 rounded-[2rem] border ${C.border} ${dk ? 'bg-white/[0.02]' : 'bg-amber-50/30'}`}>
            <VocalOrTextInput
              stepId={current.stepId || `synth-${activeStep}`}
              label={current.placeholder || "Enregistrez votre plan d'action..."}
              onSubmit={handleVocalSubmit(current.stepId || `synth-${activeStep}`)}
            />
          </div>
        )}

        {/* ── EMPOWERING CLOSURE ── */}
        {current.type === 'empowering-closure' && current.text && (
          <div className={`relative rounded-[2rem] sm:rounded-[2.5rem] border ${C.border} overflow-hidden`}>
            <div className={`absolute inset-0 ${dk
              ? 'bg-gradient-to-br from-amber-500/8 via-transparent to-transparent'
              : 'bg-gradient-to-br from-amber-50 via-white to-white'}`}
            />
            <div className="relative px-6 sm:px-12 py-10 sm:py-14 space-y-4">
              {current.text.split('\n').map((line, i) => (
                <p
                  key={i}
                  className={`font-sans normal-case leading-relaxed text-base sm:text-lg
                    ${line.startsWith('•') ? `${C.accent} font-bold` : C.text}
                    ${line === '' ? 'py-2' : ''}
                    ${i === current.text.split('\n').length - 1
                      ? `font-black text-lg sm:text-xl uppercase tracking-tight ${C.title}`
                      : ''
                    }`}
                >
                  {line || <br />}
                </p>
              ))}
            </div>
          </div>
        )}

        {/* ── Confirmation harvest local ── */}
        {localAnswers[current.stepId] && (
          <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            className={`flex items-center gap-3 text-[10px] font-mono ${C.muted}`}
          >
            <CheckCircle size={12} className="text-amber-500" />
            Réponse enregistrée localement
            {isPreview && <span className="ml-1 text-amber-400">[preview — non persistée]</span>}
          </motion.div>
        )}
      </motion.div>
    </AnimatePresence>
  );

  // ─────────────────────────────────────────────────────────────────────────
  // ASIDE CONTENT — partagé mobile/desktop
  // ─────────────────────────────────────────────────────────────────────────
  const AsideContent = () => (
    <>
      {/* Message épinglé */}
      {rt.welcomeMessage?.isPinned && (
        <div className={`p-6 sm:p-8 border-b ${C.border} ${dk ? 'bg-amber-500/5' : 'bg-amber-50/50'}`}>
          <div className={`flex items-center gap-2 mb-3 sm:mb-4 ${C.accent}`}>
            <Pin size={14} />
            <span className={`${C.label} ${C.accent}`}>Message_de_lancement</span>
          </div>
          <p className={`text-sm font-sans italic normal-case leading-relaxed ${C.text}`}>
            {rt.welcomeMessage.content}
          </p>
        </div>
      )}

      {/* Défi collectif */}
      {rt.flashChallenge?.active && (
        <div className="p-6 sm:p-8 space-y-5 sm:space-y-6">
          <div className={`p-5 sm:p-6 rounded-3xl ${dk ? 'bg-amber-600/10 border border-amber-600/20' : 'bg-amber-50 border border-amber-200'}`}>
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <Zap size={20} className={C.accent} />
              <span className={`${C.label} ${C.accent}`}>Défi_Collectif</span>
            </div>
            <h4 className={`text-base font-black leading-tight mb-2 ${C.title}`}>
              {rt.flashChallenge.title}
            </h4>
            <p className={`text-xs font-sans normal-case leading-relaxed mb-5 sm:mb-6 ${C.muted}`}>
              {rt.flashChallenge.description}
            </p>
            <div className="flex items-end justify-between">
              <span className={`text-2xl font-black ${C.accent}`}>
                +{rt.flashChallenge.bonusPoints} pts
              </span>
              <span className={`${C.label} ${C.muted}`}>J-7</span>
            </div>
          </div>

          {/* Indicateurs */}
          <div className="grid grid-cols-2 gap-3">
            <IndicateurPanel icon={<MapPin size={16} />} label="Impact local"
              status={config.impactMap ? "Actif" : "Inactif"} active={config.impactMap} darkMode={dk} />
            <IndicateurPanel icon={<MessageSquare size={16} />} label="Réseau"
              status={config.petitionTracker ? "Actif" : "Inactif"} active={config.petitionTracker} darkMode={dk} />
            <IndicateurPanel icon={<Globe size={16} />} label="Médias"
              status={config.mediaWall ? "Actif" : "Inactif"} active={config.mediaWall} darkMode={dk} />
            <IndicateurPanel icon={<Users size={16} />} label="Mentors"
              status={rt.instantFeedback?.enabled ? `< ${rt.instantFeedback.maxDelayHours}h` : "Inactif"}
              active={rt.instantFeedback?.enabled} darkMode={dk} />
          </div>
        </div>
      )}

      {/* Session finale */}
      <div className={`p-6 sm:p-8 ${!isMobile ? 'mt-auto' : ''} border-t ${C.border}`}>
        <div className={`p-5 sm:p-6 rounded-3xl border ${C.border} ${C.surface}`}>
          <div className="flex items-center gap-2 mb-3 sm:mb-4">
            <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
            <span className={`${C.label} ${C.accent}`}>Session_Nationale</span>
          </div>
          <h5 className={`text-base font-black leading-tight mb-3 sm:mb-4 uppercase ${C.title}`}>
            {liveSession.title}
          </h5>
          <div className={`flex flex-col gap-1 mb-5 sm:mb-6 text-[10px] font-mono ${C.muted}`}>
            <div className="flex justify-between">
              <span>DATE</span>
              <span className={C.text}>{liveSession.date}</span>
            </div>
            <div className="flex justify-between">
              <span>HEURE</span>
              <span className={C.text}>{liveSession.time}</span>
            </div>
          </div>
          <a
            href={liveSession.link || '#'}
            target="_blank"
            rel="noreferrer"
            className={`block w-full py-3 sm:py-4 text-center font-black text-[10px] uppercase tracking-widest rounded-xl transition-all active:scale-95
              ${dk
                ? 'bg-amber-500 text-black hover:bg-amber-400 shadow-lg shadow-amber-900/20'
                : 'bg-amber-600 text-white hover:bg-amber-500 shadow-lg shadow-amber-200'
              }`}
          >
            Rejoindre la session
          </a>
        </div>
      </div>
    </>
  );

  // ─────────────────────────────────────────────────────────────────────────
  // LAYOUT MOBILE
  // ─────────────────────────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <div className={`h-full w-full flex flex-col font-mono overflow-hidden ${C.bg} ${C.title}`}>

        {/* Header mobile compact */}
        <header className={`h-12 border-b flex items-center justify-between px-4 shrink-0 ${C.surface} ${C.border}`}>
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse shrink-0" />
            <span className={`${C.label} ${C.accent} truncate`}>{d.titre}</span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className={`text-[9px] font-black font-mono ${C.accent}`}>
              {d.points} pts
            </span>
            <div className={`text-[9px] font-mono px-2 py-0.5 rounded-full border ${C.accentBg} ${C.accent}`}>
              {activeStep + 1}/{steps.length}
            </div>
          </div>
        </header>

        {/* Stepper horizontal — remplace la nav verticale */}
        <div className={`shrink-0 px-4 py-3 border-b ${C.border} ${C.surface}`}>
          <div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
            {steps.map((s, i) => {
              const isDone   = i < activeStep;
              const isActive = i === activeStep;
              return (
                <button
                  key={i}
                  onClick={() => setActiveStep(i)}
                  title={s.title}
                  className={`shrink-0 w-8 h-8 rounded-lg border-2 flex items-center justify-center font-black text-[10px] transition-all
                    ${isActive
                      ? 'bg-amber-500 border-amber-400 text-black shadow-[0_0_12px_rgba(245,158,11,0.4)]'
                      : isDone
                        ? `border-amber-500/30 ${dk ? 'text-amber-400/60' : 'text-amber-600/60'}`
                        : `${dk ? 'border-white/10 text-slate-600' : 'border-slate-200 text-slate-400'}`
                    }`}
                >
                  {isDone ? <CheckCircle size={12} /> : String(i + 1).padStart(2, '0')}
                </button>
              );
            })}
          </div>
        </div>

        {/* Zone de contenu scrollable */}
        <main className="flex-1 overflow-y-auto no-scrollbar">
          <div className="px-4 py-6 pb-6 space-y-8">
            <StepContent />

            {/* Aside en accordéon mobile */}
            <div className={`rounded-2xl border overflow-hidden ${C.border}`}>
              <button
                onClick={() => setAsideOpen(!asideOpen)}
                className={`w-full flex items-center justify-between px-4 py-3 ${C.surface} transition-colors`}
              >
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                  <span className={`${C.label} ${C.accent}`}>Espace_Collectif</span>
                </div>
                <motion.div animate={{ rotate: asideOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
                  <ChevronDown size={14} className={C.muted} />
                </motion.div>
              </button>
              <AnimatePresence>
                {asideOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <AsideContent />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </main>

        {/* Footer navigation mobile */}
        <footer className={`h-16 border-t shrink-0 flex items-center justify-between px-4 ${C.surface} ${C.border}`}>
          <button
            onClick={handleBack}
            disabled={isFirst}
            className={`flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest transition-all disabled:opacity-0
              ${dk ? 'text-slate-500 hover:text-white' : 'text-slate-400 hover:text-slate-900'}`}
          >
            <ChevronLeft size={14} /> Préc.
          </button>

          {/* Pills de progression */}
          <div className="flex gap-1">
            {steps.map((_, i) => (
              <div
                key={i}
                className={`h-1 rounded-full transition-all duration-500
                  ${i === activeStep
                    ? 'w-6 bg-amber-500 shadow-[0_0_6px_rgba(245,158,11,0.6)]'
                    : i < activeStep ? 'w-3 bg-amber-500/40' : 'w-3 bg-white/10'
                  }`}
              />
            ))}
          </div>

          {isLast ? (
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className={`px-4 py-2.5 font-black text-[9px] uppercase tracking-widest rounded-xl transition-all active:scale-95 disabled:opacity-50
                ${dk
                  ? 'bg-amber-500 text-black hover:bg-amber-400'
                  : 'bg-amber-600 text-white hover:bg-amber-500'
                }`}
            >
              {isSubmitting ? 'Envoi...' : 'Soumettre'}
            </button>
          ) : (
            <button
              onClick={handleNext}
              className={`flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest transition-all
                ${dk ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-900'}`}
            >
              Suiv. <ChevronRight size={14} />
            </button>
          )}
        </footer>

        {/* Narrator drawer mobile */}
        <MobileNarratorDrawer
          isOpen={showNarrator}
          onClose={closeNarrator}
          mediators={mediatorOnStart || []}
          resolveAsset={resolveAsset}
          darkMode={dk}
        />
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // LAYOUT DESKTOP — inchangé
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className={`h-full w-full flex flex-col font-mono overflow-hidden ${C.bg} ${C.title}`}>

      {/* Barre de statut */}
      <header className={`h-14 border-b flex items-center justify-between px-6 shrink-0 ${C.surface} ${C.border}`}>
        <div className="flex items-center gap-5">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
            <span className={`${C.label} ${C.accent}`}>Projet_Capstone // 2026</span>
          </div>
          <div className={`h-4 w-px ${dk ? 'bg-white/10' : 'bg-slate-200'}`} />
          <span className={`${C.label} ${C.muted}`}>{d.titre}</span>
        </div>
        <div className="flex items-center gap-6">
          <div className="text-right">
            <span className={`${C.label} ${C.muted} block`}>Valeur_Projet</span>
            <span className={`text-sm font-black ${C.accent}`}>{d.points} pts</span>
          </div>
          <div className={`text-[10px] font-mono px-3 py-1 rounded-full border ${C.accentBg} ${C.accent}`}>
            {activeStep + 1} / {steps.length}
          </div>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden">

        {/* Navigation étapes (gauche) */}
        <nav className={`w-[72px] border-r flex flex-col items-center py-6 gap-3 shrink-0 ${C.surface} ${C.border}`}>
          {steps.map((s, i) => {
            const isDone   = i < activeStep;
            const isActive = i === activeStep;
            return (
              <button
                key={i}
                onClick={() => setActiveStep(i)}
                title={s.title}
                className={`w-10 h-10 rounded-xl border-2 flex items-center justify-center font-black text-[11px] transition-all
                  ${isActive
                    ? 'bg-amber-500 border-amber-400 text-black shadow-[0_0_20px_rgba(245,158,11,0.4)]'
                    : isDone
                      ? `border-amber-500/30 ${dk ? 'text-amber-400/60' : 'text-amber-600/60'}`
                      : `${dk ? 'border-white/10 text-slate-600' : 'border-slate-200 text-slate-400'} hover:border-amber-500/30`
                  }`}
              >
                {isDone ? <CheckCircle size={14} /> : String(i + 1).padStart(2, '0')}
              </button>
            );
          })}
        </nav>

        {/* Zone de travail centrale */}
        <main className="flex-1 overflow-y-auto no-scrollbar">
          <div className="max-w-3xl mx-auto px-8 py-12 pb-32 space-y-12">
            <StepContent />
          </div>
        </main>

        {/* Panneau droit */}
        <aside className={`w-[360px] border-l shrink-0 flex flex-col overflow-y-auto no-scrollbar ${C.surface} ${C.border}`}>
          <AsideContent />
        </aside>
      </div>

      {/* Footer desktop */}
      <footer className={`h-20 border-t shrink-0 flex items-center justify-between px-10 ${C.surface} ${C.border}`}>
        <button
          onClick={handleBack}
          disabled={isFirst}
          className={`flex items-center gap-2 text-[10px] font-black uppercase tracking-widest transition-all disabled:opacity-0
            ${dk ? 'text-slate-500 hover:text-white' : 'text-slate-400 hover:text-slate-900'}`}
        >
          <ChevronLeft size={16} /> Précédent
        </button>

        <div className="flex flex-col items-center gap-2">
          <div className="flex gap-1">
            {steps.map((_, i) => (
              <div
                key={i}
                className={`h-1 rounded-full transition-all duration-500
                  ${i === activeStep
                    ? 'w-8 bg-amber-500 shadow-[0_0_8px_rgba(245,158,11,0.6)]'
                    : i < activeStep ? 'w-4 bg-amber-500/40' : 'w-4 bg-white/10'
                  }`}
              />
            ))}
          </div>
          <span className={`${C.label} ${C.muted}`}>Progression_Capstone</span>
        </div>

        {isLast ? (
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className={`px-8 py-3 font-black text-[10px] uppercase tracking-widest rounded-xl transition-all active:scale-95 disabled:opacity-50
              ${dk
                ? 'bg-amber-500 text-black hover:bg-amber-400 shadow-lg shadow-amber-900/30'
                : 'bg-amber-600 text-white hover:bg-amber-500 shadow-lg shadow-amber-200'
              }`}
          >
            {isSubmitting ? 'Envoi...' : 'Soumettre mon engagement'}
          </button>
        ) : (
          <button
            onClick={handleNext}
            className={`flex items-center gap-2 text-[10px] font-black uppercase tracking-widest transition-all
              ${dk ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-900'}`}
          >
            Suivant <ChevronRight size={16} />
          </button>
        )}
      </footer>

      {/* Neural Narrator desktop */}
      <AnimatePresence>
        {showNarrator && mediatorOnStart && (
          <NeuralNarrator
            isOpen={showNarrator}
            onClose={closeNarrator}
            mediators={mediatorOnStart}
            resolveAsset={resolveAsset}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
const IndicateurPanel = ({ icon, label, status, active, darkMode: dk }) => (
  <div className={`p-4 border rounded-2xl flex flex-col gap-2 transition-all
    ${active
      ? dk ? 'border-amber-500/20 bg-amber-500/5' : 'border-amber-200 bg-amber-50'
      : dk ? 'border-white/5 bg-white/[0.01] opacity-30' : 'border-slate-100 bg-white opacity-30'
    }`}>
    <div className={active ? (dk ? 'text-amber-400' : 'text-amber-600') : (dk ? 'text-slate-600' : 'text-slate-400')}>
      {React.cloneElement(icon, { size: 16 })}
    </div>
    <div>
      <div className={`text-[8px] font-black uppercase tracking-tighter ${dk ? 'text-slate-600' : 'text-slate-400'}`}>
        {label}
      </div>
      <div className={`text-[10px] font-black ${active
        ? dk ? 'text-amber-400' : 'text-amber-600'
        : dk ? 'text-slate-600' : 'text-slate-400'}`}>
        {status}
      </div>
    </div>
  </div>
);

export default CapstoneWarRoom;